import { notifications } from '@mantine/notifications';
import { useRef, useState } from 'react';
import { generationQueueApi } from '../api/endpoints/generation-queue.js';
import type { BriefFormState } from '../state/brief-form-store.js';
import { serializeBriefForApi, useBriefFormStore } from '../state/brief-form-store.js';
import { useGenerationQueueStore } from '../state/generation-queue-store.js';
import { useRunStreamStore } from '../state/run-stream-store.js';
import { useRuntimeStore } from '../state/runtime-store.js';
import { generationModelUnavailableMessage } from '../utils/generation-models.js';

export type QueueSubmitResult =
  | { ok: true; itemId: string; runId: string | null }
  | { ok: false; itemId: null; runId: null };

interface UseCreateRunResult {
  /** True only while POST /api/generation-queue/items is in flight. */
  submitting: boolean;
  /**
   * Validates the form and snapshots the current brief into the generation queue.
   * A successful queue submit may or may not return an active run id yet.
   */
  submit: () => Promise<QueueSubmitResult>;
}

export function useCreateRun(): UseCreateRunResult {
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const form = useBriefFormStore();

  async function submit(): Promise<QueueSubmitResult> {
    if (submittingRef.current) {
      return failedSubmit();
    }

    const validationError = validateForm(form);
    if (validationError) {
      showError(validationError, form.mode);
      return failedSubmit();
    }

    const modelError = generationModelUnavailableMessage(
      form.generationModelKey,
      useRuntimeStore.getState().runtimeInfo
    );
    if (modelError) {
      showError(modelError, form.mode);
      return failedSubmit();
    }

    const brief = serializeBriefForApi(form);

    submittingRef.current = true;
    try {
      setSubmitting(true);
      const response = await generationQueueApi.add({ brief });
      useGenerationQueueStore.getState().mergeItem(response.state, response.item);
      useRunStreamStore.getState().bumpRunsListVersion();

      return {
        ok: true,
        itemId: response.item.id,
        runId: response.item.activeRunId ?? null,
      };
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to queue generation. Please try again.';
      showError(message, form.mode);
      return failedSubmit();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return { submitting, submit };
}

function validateForm(form: BriefFormState): string | null {
  if (!form.productId) return 'Please select a product.';
  if (!form.market) return 'Please select a market.';
  if (!form.angleField) return 'Please select or add an angle.';
  if (!form.avatarField) return 'Please select or add an avatar.';
  if (!form.problemMechField) return 'Please select or add a problem mechanism.';
  if (!form.solutionMechField) return 'Please select or add a solution mechanism.';
  if (form.ingredientFields.length === 0) return 'Please add at least one ingredient.';
  if (!form.awarenessLevelField) return 'Please select an awareness level.';
  if (form.mode === 'use_existing_structure' && !form.winningStructure.trim()) {
    return 'Please enter a winning structure.';
  }
  if (form.mode === 'fb_ad_to_advertorial' && !form.sourceCopy.trim()) {
    return 'Please paste the source Facebook ad.';
  }
  if (form.mode === 'competitor_advertorial_to_advertorial' && !form.sourceCopy.trim()) {
    return 'Please paste the source competitor advertorial copy.';
  }

  return null;
}

function failedSubmit(): QueueSubmitResult {
  return { ok: false, itemId: null, runId: null };
}

function showError(message: string, mode: BriefFormState['mode']) {
  notifications.show({
    color: 'red',
    title: errorTitle(mode),
    message,
    autoClose: 5000,
  });
}

function errorTitle(mode: BriefFormState['mode']): string {
  if (mode === 'scratch_premise_set') return 'Cannot generate premise set';
  if (mode === 'fb_ad_to_advertorial' || mode === 'competitor_advertorial_to_advertorial') {
    return 'Cannot generate advertorial';
  }
  return 'Cannot generate ad';
}
